import { AfterViewInit, Component, Inject, PLATFORM_ID, ErrorHandler, signal, effect } from '@angular/core';

import { RouterOutlet, ChildrenOutletContexts, Router, NavigationEnd } from '@angular/router';
import { HeaderComponent } from './pages/header/header.component';
import { FooterComponent } from './pages/footer/footer.component';
import { CookieConsentComponent } from './components/cookie-consent/cookie-consent.component';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AnimateOnScrollDirective } from './directives/animate-on-scroll.directive';
import { filter } from 'rxjs/operators';

// Animation trigger
import { routeTransitionAnimations } from './animations/route.animations';

// Import enterprise services
import { SecurityService } from './shared/services/security.service';
import { SeoService } from './shared/services/seo.service';
import { PerformanceService } from './shared/services/performance.service';
import { A11yService } from './shared/services/a11y.service';
import { GlobalErrorHandler } from './shared/error-handler/global-error.handler';
import { LoaderService } from './shared/services/loader.service';
import { MaintenanceService } from './shared/services/maintenance.service';
import { ApplicationStateService } from './shared/services/application-state.service';
import { MaintenanceComponent } from './pages/maintenance/maintenance.component';
import { Error500Component } from './pages/error500/error500.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    CookieConsentComponent,
    AnimateOnScrollDirective,
    MaintenanceComponent,
    Error500Component
],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [
    { provide: ErrorHandler, useClass: GlobalErrorHandler }
  ],
  animations: [routeTransitionAnimations]
})
export class AppComponent implements AfterViewInit {
  title = 'Articix Corporate';
  readonly loading$ = this.loader.loading$;
  showPreloader = signal(true);
  loadingProgress = signal(0);
  particles = Array.from({ length: 20 }, (_, i) => i);

  private readonly routeAnimationOrder: Record<string, number> = {
    Home: 0,
    About: 1,
    WhoWeAre: 2,
    Services: 3,
    WhatWeDo: 4,
    Industries: 5,
    AiAutomation: 6,
    Insights: 7,
    Projects: 8,
    TrustCenter: 9,
    InvestorRelations: 10,
    Careers: 11,
    Contact: 12,
    ResponsibleAi: 13,
    Accessibility: 14,
    PrivacyPolicy: 15,
    Terms: 16,
    CookiePolicy: 17,
    Error404: 18
  };

  constructor(
    private contexts: ChildrenOutletContexts,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private securityService: SecurityService,
    private seoService: SeoService,
    private performanceService: PerformanceService,
    private a11yService: A11yService,
    private loader: LoaderService,
    public router: Router,
    public maintenanceService: MaintenanceService,
    public appState: ApplicationStateService
  ) {
    this.initializeApp();

    // Effect to handle maintenance redirection
    effect(() => {
      const isMaint = this.maintenanceService.isMaintenanceEnabled();
      if (isMaint && isPlatformBrowser(this.platformId)) {
        if (this.router.url !== '/' && !this.router.url.startsWith('/#')) {
          this.router.navigate(['/'], { replaceUrl: true });
        }
      }
    });

    // Also handle navigation attempts while in maintenance
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      if (this.maintenanceService.isMaintenanceEnabled() && this.router.url !== '/') {
        this.router.navigate(['/'], { replaceUrl: true });
      }
    });
  }

  isHomeRoute(): boolean {
    return this.router.url === '/' || this.router.url.startsWith('/#') || this.router.url.startsWith('/home');
  }

  isMaintenanceRoute(): boolean {
    return this.router.url.startsWith('/maintenance');
  }

  /**
   * Returns the current route's animation identifier from `data.animation`.
   */
  getRouteAnimationData() {
    const animation = this.contexts.getContext('primary')?.route?.snapshot?.data?.['animation'];

    return typeof animation === 'string'
      ? this.routeAnimationOrder[animation] ?? animation
      : animation;
  }

  /**
   * Initialize application with all enterprise services
   */
  private initializeApp(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.startLoadingSimulation();

    // Initialize Security
    this.securityService.disableConsoleInProduction();

    // Initialize SEO with default values
    this.seoService.updatePageMeta({
      title: 'Articix - Enterprise Technology Partner',
      description: 'Digital transformation solutions including AI, cloud engineering, and enterprise web platforms.',
      keywords: ['enterprise software', 'digital transformation', 'AI automation', 'cloud solutions'],
      canonicalUrl: 'https://articix.com',
      schema: this.seoService.getOrganizationSchema()
    });

    // Initialize Performance Monitoring
    // Wrapped in requestIdleCallback if available, otherwise setTimeout
    const initPerformance = () => {
      const metrics = this.performanceService.getPerformanceMetrics();
      this.securityService.logSecurityEvent('APP_PERFORMANCE_METRICS', metrics);
    };

    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(initPerformance);
    } else {
      setTimeout(initPerformance, 3000);
    }
  }

  private startLoadingSimulation(): void {
    let currentProgress = 0;
    const interval = setInterval(() => {
      // Faster initial load, then slows down as it approaches 95%
      const rand = typeof crypto !== 'undefined' && crypto.getRandomValues ? (crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296) : Math.random(); // NOSONAR: Non-cryptographic visual preloader simulation
      const increment = currentProgress < 70 ? rand * 15 : rand * 5;
      currentProgress += increment;

      if (currentProgress >= 95) {
        currentProgress = 95;
        clearInterval(interval);
      }
      this.loadingProgress.set(Math.floor(currentProgress));
    }, 80);
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const completeLoading = () => {
      if (!this.showPreloader()) return;

      this.loadingProgress.set(100);

      setTimeout(() => {
        this.showPreloader.set(false);
        this.loader.hide();
      }, 400);
    };

    if (this.document.readyState === 'complete') {
      completeLoading();
    } else {
      window.addEventListener('load', completeLoading, { once: true });
    }

    // Safety timeout to ensure preloader doesn't stay forever if 'load' event fails
    setTimeout(completeLoading, 5000);
  }
}
